import { useState, type ReactNode } from 'react';
import type { MovieFilters } from '@/utils/movieQuery';

interface FilterPanelProps {
  filters: MovieFilters;
  onChange: (next: MovieFilters) => void;
  onReset: () => void;
}

const YEAR_OPTIONS: { value: NonNullable<MovieFilters['yearRange']>; label: string }[] = [
  { value: 'recent3', label: '最近 3 年' },
  { value: 'recent5', label: '最近 5 年' },
  { value: 'all', label: '全部年份' },
];

const RATING_OPTIONS = [
  { value: 0, label: '不限评分' },
  { value: 8, label: '8 分以上' },
  { value: 8.5, label: '8.5 分以上' },
];

const DURATION_OPTIONS: { value: NonNullable<MovieFilters['durationRange']>; label: string }[] = [
  { value: 'all', label: '全部时长' },
  { value: 'short', label: '90 分钟以内' },
  { value: 'medium', label: '90 - 120 分钟' },
  { value: 'long', label: '120 分钟以上' },
];

export default function FilterPanel({ filters, onChange, onReset }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  const chip = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition ${
      active
        ? 'bg-primary-500 text-white'
        : 'bg-background-200/70 text-foreground-600 hover:bg-background-300/70 hover:text-foreground-950'
    }`;

  const Group = ({
    title,
    children,
  }: {
    title: string;
    children: ReactNode;
  }) => (
    <div>
      <p className="mb-2.5 text-xs text-foreground-500">{title}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-300/50 bg-background-100">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-sm text-foreground-800 md:cursor-default"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-400"></i>
          筛选条件
        </span>
        <i className={`ri-arrow-down-s-line text-lg transition md:hidden ${open ? 'rotate-180' : ''}`}></i>
      </button>

      <div className={`${open ? 'block' : 'hidden'} border-t border-background-300/40 px-5 py-5 md:block md:border-t-0 md:pt-0`}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Group title="上映年份">
            {YEAR_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...filters, yearRange: opt.value })}
                className={chip((filters.yearRange || 'all') === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </Group>

          <Group title="评分区间">
            {RATING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...filters, minRating: opt.value })}
                className={chip((filters.minRating || 0) === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </Group>

          <Group title="影片时长">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...filters, durationRange: opt.value })}
                className={chip((filters.durationRange || 'all') === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </Group>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition hover:text-primary-400"
          >
            <i className="ri-refresh-line"></i>重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}